// src/utils/errors.ts
var WebChatError = class extends Error {
  constructor(code, message, options) {
    super(message);
    this.name = "WebChatError";
    this.code = code;
    this.cause = options?.cause;
    this.status = options?.status;
  }
};
function toWebChatError(error) {
  if (error instanceof WebChatError) {
    return error;
  }
  if (error instanceof DOMException && error.name === "AbortError") {
    return new WebChatError("TIMEOUT", "Request timed out.", { cause: error });
  }
  if (error instanceof TypeError) {
    return new WebChatError("NETWORK", error.message || "Network request failed.", {
      cause: error
    });
  }
  if (error instanceof Error) {
    return new WebChatError("UNKNOWN", error.message, { cause: error });
  }
  return new WebChatError("UNKNOWN", "An unknown error occurred.", {
    cause: error
  });
}

// src/client/RestClient.ts
var WEBCHAT_PUBLIC_KEY_HEADER = "x-webchat-public-key";
var RestClient = class {
  constructor(options) {
    this.apiUrl = options.apiUrl.replace(/\/+$/, "");
    this.publicKey = options.publicKey?.trim() || void 0;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 15e3;
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  }
  async initializeSession(body) {
    const publicKey = body.publicKey?.trim() || this.publicKey;
    const path = publicKey != null && publicKey.length > 0 ? `/webchat/init?publicKey=${encodeURIComponent(publicKey)}` : "/webchat/init";
    return this.postJson(path, body);
  }
  async postJson(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.requestTimeoutMs);
    try {
      const response = await this.fetchImpl(this.resolveUrl(path), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...this.publicKey ? { [WEBCHAT_PUBLIC_KEY_HEADER]: this.publicKey } : {}
        },
        body: JSON.stringify(body),
        signal: controller.signal
      });
      if (!response.ok) {
        const detail = await this.safeReadBody(response);
        throw this.mapHttpError(response.status, detail);
      }
      return await response.json();
    } catch (error) {
      throw toWebChatError(error);
    } finally {
      clearTimeout(timeout);
    }
  }
  resolveUrl(path) {
    return `${this.apiUrl}/${path.replace(/^\/+/, "")}`;
  }
  mapHttpError(status, detail) {
    if (status === 401 || status === 403) {
      return new WebChatError(
        "AUTHENTICATION",
        detail || "Authentication failed.",
        { status }
      );
    }
    if (status === 404) {
      return new WebChatError(
        "INVALID_SESSION",
        detail || "Session or resource not found.",
        { status }
      );
    }
    if (status === 400 || status === 422) {
      return new WebChatError("VALIDATION", detail || "Invalid request.", {
        status
      });
    }
    if (status >= 500) {
      return new WebChatError("NETWORK", detail || "Server error.", { status });
    }
    return new WebChatError("UNKNOWN", detail || `HTTP ${status}`, { status });
  }
  async safeReadBody(response) {
    try {
      const data = await response.json();
      if (data && typeof data === "object") {
        const record = data;
        if (typeof record.message === "string") {
          return record.message;
        }
        if (Array.isArray(record.message) && typeof record.message[0] === "string") {
          return record.message[0];
        }
      }
      return JSON.stringify(data);
    } catch {
      try {
        return await response.text();
      } catch {
        return "";
      }
    }
  }
};

// src/events/EventEmitter.ts
var EventEmitter = class {
  constructor() {
    this.listeners = /* @__PURE__ */ new Map();
  }
  on(event, handler) {
    let set = this.listeners.get(event);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this.listeners.set(event, set);
    }
    set.add(handler);
    return () => this.off(event, handler);
  }
  once(event, handler) {
    const wrap = (payload) => {
      this.off(event, wrap);
      handler(payload);
    };
    return this.on(event, wrap);
  }
  off(event, handler) {
    this.listeners.get(event)?.delete(handler);
  }
  emit(event, payload) {
    const set = this.listeners.get(event);
    if (!set) {
      return;
    }
    for (const handler of [...set]) {
      handler(payload);
    }
  }
  removeAllListeners(event) {
    if (event) {
      this.listeners.delete(event);
      return;
    }
    this.listeners.clear();
  }
};

// src/session/SessionStore.ts
var VISITOR_KEY = "revocom.webchat.visitorId";
var SESSION_KEY = "revocom.webchat.session";
var MemoryStorage = class {
  constructor() {
    this.map = /* @__PURE__ */ new Map();
  }
  getItem(key) {
    return this.map.get(key) ?? null;
  }
  setItem(key, value) {
    this.map.set(key, value);
  }
  removeItem(key) {
    this.map.delete(key);
  }
};
var SessionStore = class {
  constructor(storage = new MemoryStorage()) {
    this.storage = storage;
    this.session = null;
  }
  get() {
    return this.session;
  }
  require() {
    if (!this.session) {
      throw new Error("WebChat session is not initialized.");
    }
    return this.session;
  }
  set(session) {
    this.session = session;
  }
  clear() {
    this.session = null;
  }
  async loadPersistedVisitorId() {
    const value = await this.storage.getItem(VISITOR_KEY);
    return value?.trim() || void 0;
  }
  async persistVisitorId(visitorId) {
    await this.storage.setItem(VISITOR_KEY, visitorId);
  }
  async persistSession(session) {
    this.session = session;
    await this.storage.setItem(
      SESSION_KEY,
      JSON.stringify({
        visitorId: session.visitorId,
        sessionId: session.sessionId,
        conversationId: session.conversationId ?? null,
        sessionToken: session.sessionToken,
        expiresAt: session.expiresAt.toISOString()
      })
    );
    await this.persistVisitorId(session.visitorId);
  }
  async loadPersistedSession() {
    const raw = await this.storage.getItem(SESSION_KEY);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      const expiresAt = new Date(parsed.expiresAt);
      if (Number.isNaN(expiresAt.getTime()) || expiresAt.getTime() <= Date.now()) {
        await this.storage.removeItem(SESSION_KEY);
        return null;
      }
      const session = {
        visitorId: parsed.visitorId,
        sessionId: parsed.sessionId,
        conversationId: parsed.conversationId ?? null,
        sessionToken: parsed.sessionToken,
        expiresAt
      };
      this.session = session;
      return session;
    } catch {
      await this.storage.removeItem(SESSION_KEY);
      return null;
    }
  }
  async clearPersisted() {
    this.session = null;
    await this.storage.removeItem(SESSION_KEY);
  }
};

// src/types/events.ts
var WebChatEvents = {
  MESSAGE_CREATED: "message.created",
  MESSAGE_DELIVERED: "message.delivered",
  MESSAGE_READ: "message.read",
  TYPING_STARTED: "typing:started",
  TYPING_STOPPED: "typing:stopped",
  PRESENCE_ONLINE: "presence.online",
  PRESENCE_OFFLINE: "presence.offline",
  PRESENCE_UPDATED: "presence.updated",
  CONVERSATION_STATUS_UPDATED: "conversation.status.updated",
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  RECONNECTED: "reconnected",
  ERROR: "error"
};

// src/websocket/SocketManager.ts
import { io } from "socket.io-client";
var SocketManager = class {
  constructor(options) {
    this.options = options;
    this.socket = null;
    this.session = null;
    this.joinedConversationId = null;
    this.intentionalDisconnect = false;
    this.handlers = /* @__PURE__ */ new Map();
  }
  get connected() {
    return this.socket?.connected === true;
  }
  async connect(session) {
    this.session = session;
    this.intentionalDisconnect = false;
    if (this.socket?.connected) {
      return;
    }
    if (this.socket) {
      this.socket.auth = { sessionToken: session.sessionToken };
      this.socket.connect();
      await this.waitForConnect(this.socket);
      return;
    }
    const namespace = this.options.namespace ?? "/webchat";
    const publicKey = this.options.publicKey?.trim();
    const socket = io(`${this.options.websocketUrl}${namespace}`, {
      transports: ["websocket"],
      auth: { sessionToken: session.sessionToken },
      query: publicKey ? { publicKey } : void 0,
      reconnection: true,
      reconnectionAttempts: this.options.reconnectionAttempts ?? Infinity,
      reconnectionDelay: this.options.reconnectionDelay ?? 1e3,
      reconnectionDelayMax: this.options.reconnectionDelayMax ?? 1e4,
      autoConnect: false
    });
    this.bindSocketLifecycle(socket);
    this.socket = socket;
    socket.connect();
    await this.waitForConnect(socket);
  }
  async joinConversation(conversationId) {
    this.requireSocket();
    const id = conversationId ?? this.session?.conversationId;
    if (!id) {
      throw new WebChatError(
        "INVALID_SESSION",
        "No conversation id available to join."
      );
    }
    await this.emitAck("join", { conversationId: id });
    this.joinedConversationId = id;
  }
  async sendMessage(content) {
    const conversationId = this.joinedConversationId ?? this.session?.conversationId;
    return this.emitAck("message.send", {
      ...conversationId ? { conversationId } : {},
      content
    });
  }
  /**
   * Reopen the persistent visitor conversation after CLOSE.
   * Inserts a History separator; never creates a new conversation.
   */
  async reopenConversation(conversationId) {
    const id = conversationId ?? this.joinedConversationId ?? this.session?.conversationId;
    return this.emitAck("conversation.reopen", {
      ...id ? { conversationId: id } : {}
    });
  }
  async startTyping() {
    await this.emitAck(WebChatEvents.TYPING_STARTED, {
      conversationId: this.requireConversationId()
    });
  }
  async stopTyping() {
    await this.emitAck(WebChatEvents.TYPING_STOPPED, {
      conversationId: this.requireConversationId()
    });
  }
  async markDelivered(messageId) {
    return this.emitAck("message.delivered", {
      conversationId: this.requireConversationId(),
      messageId
    });
  }
  async markRead(messageId) {
    return this.emitAck("message.read", {
      conversationId: this.requireConversationId(),
      messageId
    });
  }
  updateSession(session) {
    this.session = session;
    if (session.conversationId) {
      this.joinedConversationId = session.conversationId;
    }
  }
  disconnect() {
    this.intentionalDisconnect = true;
    this.joinedConversationId = null;
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }
  onServerEvent(event, handler) {
    let set = this.handlers.get(event);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this.handlers.set(event, set);
      this.socket?.on(event, (payload) => this.dispatch(event, payload));
    }
    set.add(handler);
    return () => {
      set?.delete(handler);
    };
  }
  bindSocketLifecycle(socket) {
    for (const event of [
      WebChatEvents.MESSAGE_CREATED,
      WebChatEvents.MESSAGE_DELIVERED,
      WebChatEvents.MESSAGE_READ,
      WebChatEvents.TYPING_STARTED,
      WebChatEvents.TYPING_STOPPED,
      WebChatEvents.PRESENCE_ONLINE,
      WebChatEvents.PRESENCE_OFFLINE,
      WebChatEvents.PRESENCE_UPDATED,
      WebChatEvents.CONVERSATION_STATUS_UPDATED
    ]) {
      socket.on(event, (payload) => this.dispatch(event, payload));
    }
    socket.on("disconnect", (reason) => {
      if (!this.intentionalDisconnect) {
        this.dispatch("__disconnected__", { reason });
      }
    });
    socket.on("reconnect", () => {
      void this.rejoinAfterReconnect();
    });
  }
  async rejoinAfterReconnect() {
    if (!this.session || this.intentionalDisconnect) {
      return;
    }
    if (this.socket) {
      this.socket.auth = { sessionToken: this.session.sessionToken };
    }
    const conversationId = this.joinedConversationId ?? this.session.conversationId;
    if (conversationId && this.socket?.connected) {
      try {
        await this.emitAck("join", { conversationId });
        this.joinedConversationId = conversationId;
        this.dispatch("__reconnected__", { session: this.session });
      } catch {
        this.dispatch("__error__", {
          error: new WebChatError(
            "CONNECTION",
            "Failed to rejoin conversation after reconnect."
          )
        });
      }
    }
  }
  dispatch(event, payload) {
    const set = this.handlers.get(event);
    if (!set) {
      return;
    }
    for (const handler of [...set]) {
      handler(payload);
    }
  }
  waitForConnect(socket) {
    if (socket.connected) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const onConnect = () => {
        cleanup();
        resolve();
      };
      const onError = (err) => {
        cleanup();
        reject(
          new WebChatError("AUTHENTICATION", err.message || "Connection failed.", {
            cause: err
          })
        );
      };
      const onDisconnect = (reason) => {
        cleanup();
        reject(
          new WebChatError("CONNECTION", `Disconnected during connect: ${reason}`)
        );
      };
      const cleanup = () => {
        socket.off("connect", onConnect);
        socket.off("connect_error", onError);
        socket.off("disconnect", onDisconnect);
      };
      socket.once("connect", onConnect);
      socket.once("connect_error", onError);
      socket.once("disconnect", onDisconnect);
    });
  }
  emitAck(event, payload) {
    const socket = this.requireSocket();
    return new Promise((resolve, reject) => {
      socket.timeout(1e4).emit(event, payload, (err, response) => {
        if (err) {
          reject(
            new WebChatError("CONNECTION", err.message || `Event ${event} failed.`, {
              cause: err
            })
          );
          return;
        }
        resolve(response);
      });
    });
  }
  requireSocket() {
    if (!this.socket?.connected) {
      throw new WebChatError("CONNECTION", "WebSocket is not connected.");
    }
    return this.socket;
  }
  requireConversationId() {
    const id = this.joinedConversationId ?? this.session?.conversationId;
    if (!id) {
      throw new WebChatError(
        "INVALID_SESSION",
        "No active conversation. Call joinConversation() first."
      );
    }
    return id;
  }
};

// src/WebChatClient.ts
var WebChatClient = class {
  constructor(options) {
    this.emitter = new EventEmitter();
    this.initialized = false;
    this.conversationJoined = false;
    if (!options.apiUrl?.trim()) {
      throw new WebChatError("VALIDATION", "apiUrl is required.");
    }
    if (!options.websocketUrl?.trim()) {
      throw new WebChatError("VALIDATION", "websocketUrl is required.");
    }
    if (!options.publicKey?.trim() && !options.organizationId?.trim()) {
      throw new WebChatError(
        "VALIDATION",
        "publicKey or organizationId is required."
      );
    }
    this.options = options;
    this.rest = new RestClient({
      apiUrl: options.apiUrl,
      publicKey: options.publicKey,
      requestTimeoutMs: options.requestTimeoutMs
    });
    this.sockets = new SocketManager({
      websocketUrl: options.websocketUrl.replace(/\/+$/, ""),
      namespace: options.namespace ?? "/webchat",
      publicKey: options.publicKey,
      reconnectionAttempts: options.reconnectionAttempts,
      reconnectionDelay: options.reconnectionDelay,
      reconnectionDelayMax: options.reconnectionDelayMax
    });
    this.sessions = new SessionStore(options.storage);
    this.bindSocketBridge();
  }
  /** Current session, or `null` before {@link initialize}. */
  get session() {
    return this.sessions.get();
  }
  get isConnected() {
    return this.sockets.connected;
  }
  get conversationId() {
    return this.sessions.get()?.conversationId ?? null;
  }
  /**
   * Initialize (or resume) a visitor session via `POST /webchat/init`.
   * Persists visitor/session identifiers when storage is configured.
   */
  async initialize() {
    const persistedVisitorId = this.options.visitorId ?? await this.sessions.loadPersistedVisitorId();
    try {
      return await this.initializeWithVisitorId(persistedVisitorId);
    } catch (error) {
      const mapped = toWebChatError(error);
      const shouldRetry = persistedVisitorId != null && persistedVisitorId.trim().length > 0 && this.shouldRetryWithoutPersistedVisitor(mapped);
      if (!shouldRetry) {
        this.emitter.emit(WebChatEvents.ERROR, { error: mapped });
        throw mapped;
      }
      this.sessions.clear();
      await this.sessions.clearPersisted();
      try {
        return await this.initializeWithVisitorId(void 0);
      } catch (retryError) {
        const retryMapped = toWebChatError(retryError);
        this.emitter.emit(WebChatEvents.ERROR, { error: retryMapped });
        throw retryMapped;
      }
    }
  }
  async initializeWithVisitorId(visitorId) {
    try {
      const response = await this.rest.initializeSession({
        publicKey: this.options.publicKey,
        organizationId: this.options.organizationId,
        visitorId,
        displayName: this.options.displayName,
        email: this.options.email,
        phoneNumber: this.options.phoneNumber,
        providerId: this.options.providerId,
        metadata: this.options.metadata
      });
      const session = {
        visitorId: response.visitorToken ?? response.visitorId,
        sessionId: response.sessionId,
        conversationId: response.conversationId ?? null,
        sessionToken: response.socketToken ?? response.sessionToken,
        expiresAt: new Date(response.expiresAt),
        conversationStatus: response.conversationStatus,
        initialMessages: response.messages
      };
      await this.sessions.persistSession(session);
      this.initialized = true;
      this.conversationJoined = false;
      return session;
    } catch (error) {
      throw toWebChatError(error);
    }
  }
  shouldRetryWithoutPersistedVisitor(error) {
    if (error.code === "INVALID_SESSION") {
      return true;
    }
    if (error.code !== "VALIDATION") {
      return false;
    }
    const message = error.message.toLowerCase();
    if (message.includes("name is required") || message.includes("email is required") || message.includes("phone is required") || message.includes("email format") || message.includes("phone format")) {
      return false;
    }
    return message.includes("visitor");
  }
  /**
   * Open the WebSocket connection using the session token from {@link initialize}.
   */
  async connect() {
    const session = this.requireInitialized();
    try {
      await this.sockets.connect(session);
      this.emitter.emit(WebChatEvents.CONNECTED, { session });
    } catch (error) {
      const mapped = toWebChatError(error);
      this.emitter.emit(WebChatEvents.ERROR, { error: mapped });
      throw mapped;
    }
  }
  /**
   * Join the active conversation room (defaults to the session conversation).
   */
  async joinConversation(conversationId) {
    this.requireInitialized();
    try {
      await this.sockets.joinConversation(conversationId);
      this.conversationJoined = true;
      const current = this.sessions.get();
      const resolvedId = conversationId ?? current?.conversationId;
      if (current && resolvedId && current.conversationId !== resolvedId) {
        const next = { ...current, conversationId: resolvedId };
        this.sessions.set(next);
        this.sockets.updateSession(next);
        await this.sessions.persistSession(next);
      }
    } catch (error) {
      const mapped = toWebChatError(error);
      this.emitter.emit(WebChatEvents.ERROR, { error: mapped });
      throw mapped;
    }
  }
  /**
   * Convenience: initialize → connect → join conversation.
   */
  async start() {
    const session = await this.initialize();
    await this.connect();
    if (session.conversationId) {
      await this.joinConversationWithRetry(session.conversationId);
    }
    return session;
  }
  /**
   * Reopen the same persistent visitor conversation after CLOSE.
   * Inserts a History separator system message; never creates a new conversation.
   */
  async reopenConversation() {
    this.requireConnected();
    try {
      const result = await this.sockets.reopenConversation();
      await this.applyConversationFromReopenResult(result);
      return result;
    } catch (error) {
      const mapped = toWebChatError(error);
      this.emitter.emit(WebChatEvents.ERROR, { error: mapped });
      throw mapped;
    }
  }
  /**
   * Reopen the persistent visitor conversation after CLOSE.
   * @deprecated Prefer {@link reopenConversation}.
   */
  async startNewConversation() {
    const result = await this.reopenConversation();
    const session = this.sessions.get();
    if (!session) {
      throw new WebChatError("INVALID_SESSION", "Session is not initialized.");
    }
    return {
      ...session,
      conversationId: result.conversationId,
      conversationStatus: result.status
    };
  }
  async sendMessage(content) {
    this.requireConnected();
    const trimmed = content?.trim();
    if (!trimmed) {
      throw new WebChatError("VALIDATION", "Message content is required.");
    }
    try {
      const result = await this.sockets.sendMessage(trimmed);
      await this.applyConversationFromSendResult(result);
      return result;
    } catch (error) {
      const mapped = toWebChatError(error);
      this.emitter.emit(WebChatEvents.ERROR, { error: mapped });
      throw mapped;
    }
  }
  async startTyping() {
    this.requireJoined();
    await this.sockets.startTyping();
  }
  async stopTyping() {
    this.requireJoined();
    await this.sockets.stopTyping();
  }
  async markDelivered(messageId) {
    this.requireJoined();
    return this.sockets.markDelivered(messageId);
  }
  async markRead(messageId) {
    this.requireJoined();
    return this.sockets.markRead(messageId);
  }
  /**
   * Subscribe to a SDK event. Returns an unsubscribe function.
   */
  on(event, handler) {
    return this.emitter.on(event, handler);
  }
  once(event, handler) {
    return this.emitter.once(event, handler);
  }
  off(event, handler) {
    this.emitter.off(event, handler);
  }
  /**
   * Close the WebSocket connection. Session data remains until {@link clearSession}.
   */
  disconnect() {
    this.sockets.disconnect();
    this.conversationJoined = false;
    this.emitter.emit(WebChatEvents.DISCONNECTED, {});
  }
  /** Clear in-memory and persisted session state. */
  async clearSession() {
    this.disconnect();
    await this.sessions.clearPersisted();
    this.initialized = false;
  }
  bindSocketBridge() {
    const forward = (event) => {
      this.sockets.onServerEvent(event, (payload) => {
        this.emitter.emit(event, payload);
      });
    };
    forward(WebChatEvents.MESSAGE_CREATED);
    forward(WebChatEvents.MESSAGE_DELIVERED);
    forward(WebChatEvents.MESSAGE_READ);
    forward(WebChatEvents.TYPING_STARTED);
    forward(WebChatEvents.TYPING_STOPPED);
    forward(WebChatEvents.PRESENCE_ONLINE);
    forward(WebChatEvents.PRESENCE_OFFLINE);
    forward(WebChatEvents.PRESENCE_UPDATED);
    forward(WebChatEvents.CONVERSATION_STATUS_UPDATED);
    this.emitter.on(WebChatEvents.CONVERSATION_STATUS_UPDATED, (payload) => {
      const current = this.sessions.get();
      if (!current?.conversationId) {
        return;
      }
      if (payload.conversationId !== current.conversationId) {
        return;
      }
      const next = {
        ...current,
        conversationStatus: payload.status
      };
      this.sessions.set(next);
      void this.sessions.persistSession(next);
      this.sockets.updateSession(next);
    });
    this.sockets.onServerEvent("__disconnected__", (payload) => {
      this.conversationJoined = false;
      this.emitter.emit(WebChatEvents.DISCONNECTED, payload);
    });
    this.sockets.onServerEvent("__reconnected__", (payload) => {
      this.conversationJoined = true;
      const session = payload.session;
      this.emitter.emit(WebChatEvents.RECONNECTED, { session });
    });
    this.sockets.onServerEvent("__error__", (payload) => {
      this.emitter.emit(WebChatEvents.ERROR, payload);
    });
  }
  requireInitialized() {
    if (!this.initialized || !this.sessions.get()) {
      throw new WebChatError(
        "INVALID_SESSION",
        "Call initialize() before connecting."
      );
    }
    return this.sessions.require();
  }
  requireConnected() {
    this.requireInitialized();
    if (!this.sockets.connected) {
      throw new WebChatError("CONNECTION", "WebSocket is not connected.");
    }
  }
  async applyConversationFromSendResult(result) {
    const current = this.sessions.get();
    if (!current) {
      return;
    }
    const next = {
      ...current,
      conversationId: result.conversationId,
      conversationStatus: result.status ?? current.conversationStatus ?? "OPEN"
    };
    this.sessions.set(next);
    this.sockets.updateSession(next);
    await this.sessions.persistSession(next);
    if (result.conversationCreated || this.conversationId !== result.conversationId) {
      await this.joinConversation(result.conversationId);
    } else if (!this.conversationJoined) {
      await this.joinConversation(result.conversationId);
    }
  }
  async applyConversationFromReopenResult(result) {
    const current = this.sessions.get();
    if (!current) {
      return;
    }
    const next = {
      ...current,
      conversationId: result.conversationId,
      conversationStatus: result.status
    };
    this.sessions.set(next);
    this.sockets.updateSession(next);
    await this.sessions.persistSession(next);
    if (!this.conversationJoined || this.conversationId !== result.conversationId) {
      await this.joinConversation(result.conversationId);
    }
  }
  requireJoined() {
    this.requireConnected();
    if (!this.conversationJoined) {
      throw new WebChatError(
        "INVALID_SESSION",
        "Call joinConversation() before sending typing indicators."
      );
    }
  }
  async joinConversationWithRetry(conversationId) {
    const maxAttempts = 4;
    let lastError = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        await this.joinConversation(conversationId);
        return;
      } catch (error) {
        const mapped = toWebChatError(error);
        lastError = mapped;
        if (!this.isRetryableJoinError(mapped) || attempt === maxAttempts) {
          throw mapped;
        }
        await this.delay(attempt * 200);
      }
    }
    if (lastError) {
      throw lastError;
    }
  }
  isRetryableJoinError(error) {
    if (error.code === "INVALID_SESSION") {
      return true;
    }
    if (error.code !== "CONNECTION") {
      return false;
    }
    const message = error.message.toLowerCase();
    return message.includes("session") || message.includes("not found") || message.includes("not active");
  }
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
};

// src/utils/backoff.ts
function computeBackoffDelay(attempt, options) {
  const baseMs = options?.baseMs ?? 1e3;
  const maxMs = options?.maxMs ?? 1e4;
  const jitterRatio = options?.jitterRatio ?? 0.2;
  const exp = Math.min(maxMs, baseMs * 2 ** Math.max(0, attempt - 1));
  const jitter = exp * jitterRatio * Math.random();
  return Math.min(maxMs, Math.floor(exp + jitter));
}
export {
  EventEmitter,
  MemoryStorage,
  RestClient,
  SessionStore,
  SocketManager,
  WebChatClient,
  WebChatError,
  WebChatEvents,
  computeBackoffDelay,
  toWebChatError
};
